import PrivateChatPhoto from "./PrivateChatPhoto";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { createPortal } from "react-dom";
import { Trash2 } from "lucide-react";
import api from "../../services/api";

const MessageBubble = ({ message, isOwn, senderName, onPhotoDeleted }) => {
  // HAPPYCAP_PHOTO_DELETE_BUTTON_V1
  const { t } = useTranslation();
  const [deletingPhoto, setDeletingPhoto] = useState(false);
  // HAPPYCAP_CHAT_DELETE_PHOTO_MODAL_V1
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const photoId = String(message.id ?? "")
    .match(/^photo_([1-9][0-9]*)$/)?.[1];

  const handleDeletePhoto = async () => {
    if (!isOwn || !photoId || deletingPhoto) return;

    setDeletingPhoto(true);

    try {
      await api.delete(`/chat/fotos/${photoId}/`);

      if (onPhotoDeleted) {
        onPhotoDeleted(message.id);
      }
    } catch (error) {
      console.error("Error eliminando fotografía", error);
      window.alert(
        "No se pudo eliminar la fotografía. Inténtalo de nuevo."
      );
    } finally {
      setDeletingPhoto(false);
      setShowDeleteModal(false);
    }
  };

  const fecha = message.timestamp ? new Date(message.timestamp) : null;

  const formattedTime =
    fecha && !Number.isNaN(fecha.getTime())
      ? fecha.toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        })
      : "";

  return (
    <>
    <div
      className={`mb-2 flex w-full px-1 ${
        isOwn ? "justify-end" : "justify-start"
      }`}
    >
      <div
        className={`max-w-[88%] min-w-[75px] break-words rounded-2xl px-3.5 py-2.5 text-[14px] leading-relaxed shadow-sm sm:max-w-[72%] ${
          isOwn
            ? "rounded-br-md bg-[#dcf8e7] text-slate-800"
            : "rounded-bl-md border border-slate-100 bg-white text-slate-800"
        }`}
      >
        {!isOwn && senderName && (
          <div className="mb-1 text-xs font-semibold text-[#247BA8]">
            {senderName}
          </div>
        )}

        {/* HAPPYCAP_PRIVATE_PHOTO_DISPLAY_V2 */}
        {message.type === "photo" ||
        String(message.id ?? "").startsWith("photo_") ? (
          <div>
            <PrivateChatPhoto message={message} />


          </div>
        ) : (
          <p className="whitespace-pre-wrap break-words">
            {message.message}
          </p>
        )}

        <div className="mt-1 flex items-center justify-end gap-1.5">
          {/* HAPPYCAP_PHOTO_TRASH_NEXT_TO_TIME_V1 */}
          {isOwn && photoId && (
            <button
              type="button"
              onClick={() => setShowDeleteModal(true)}
              disabled={deletingPhoto}
              className="mr-1 inline-flex h-7 w-7 items-center justify-center rounded-full text-red-500 hover:bg-red-50 hover:text-red-700 disabled:opacity-50"
              aria-label="Eliminar fotografía para todos"
              title="Eliminar para todos"
            >
              <Trash2 size={15} />
            </button>
          )}

          <span className="text-[10px] text-slate-500">
            {formattedTime}
          </span>

          {isOwn && (
            <span
              className={`text-[11px] font-semibold ${
                message.read ? "text-[#247BA8]" : "text-slate-400"
              }`}
              title={message.read ? "Leído" : "Enviado"}
            >
              {message.read ? "✓✓" : "✓"}
            </span>
          )}
        </div>
      </div>
    </div>

      {/* HAPPYCAP_CHAT_DELETE_PHOTO_MODAL_V1 */}
      {showDeleteModal && createPortal(
        <div
          className="fixed inset-0 z-[20000] flex items-center justify-center bg-black/60 px-4 py-6"
          onClick={() => {
            if (!deletingPhoto) setShowDeleteModal(false);
          }}
          role="presentation"
        >
          <div
            role="alertdialog"
            aria-modal="true"
            aria-labelledby={`delete-photo-title-${photoId}`}
            aria-describedby={`delete-photo-text-${photoId}`}
            className="w-full max-w-sm max-h-[85vh] overflow-y-auto rounded-2xl bg-white p-6 text-center shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h3
              id={`delete-photo-title-${photoId}`}
              className="text-lg font-semibold mb-4"
            >
              {t("image_gallery.confirm_delete_title")}
            </h3>

            <p
              id={`delete-photo-text-${photoId}`}
              className="mb-6 text-gray-600"
            >
              {t("image_gallery.confirm_delete_text")}
            </p>

            <div className="flex flex-col gap-3 sm:flex-row sm:justify-center">
              <button
                type="button"
                onClick={handleDeletePhoto}
                disabled={deletingPhoto}
                className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 transition disabled:opacity-50"
              >
                {deletingPhoto ? "…" : t("image_gallery.confirm")}
              </button>

              <button
                type="button"
                onClick={() => setShowDeleteModal(false)}
                disabled={deletingPhoto}
                className="bg-gray-300 text-gray-800 px-4 py-2 rounded hover:bg-gray-400 transition disabled:opacity-50"
              >
                {t("image_gallery.cancel")}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default MessageBubble;
